import React, { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppSelector } from '../../../store/configureStore';

import ActivateProjectForm from '../../forms/activateProjectForm';
import AcceptReportForm from '../../forms/acceptReportForm';
import AuditTrail from '../../blocks/audit-trail';
import ApproveReportForm from '../../forms/approveReportForm';
import Button from '../../themed/button';
import ClientReviewForm from '../../forms/clientReviewForm';
import Conditional from '../../common/conditional';
import UpdateProject from '../../forms/createProject';
import Elevation from '../../themed/instruction-modals/elevation';
import Header from '../../blocks/header';
import Image from '../../themed/image';
import Loader from '../../themed/loader';
import Modal from '../../themed/modal';
import Notice from '../../themed/notice';
import NotificationBell from '../../themed/notification-bell';
import ProjectStatusButton from '../../themed/project-status-button';
import ProjectComments from '../../blocks/project-comments';
import ProjectSummary from '../../blocks/project-summary';
import SyncTime from '../../blocks/sync-time';
import ProjectDetailsInstruction from '../../themed/instruction-modals/projectDetailsInstruction';
import RequestSupervisorUpdate from '../../blocks/request-supervisor-update';
import ReviewReportForm from '../../forms/reviewReportForm';
import Status from '../../blocks/status';
import Timeline from '../../themed/tiimeline';
import UploadReportPDF from '../../forms/uploadReportPDF';
import DuplicateReport from '../../blocks/duplicate-report';
import ReportAnomaly from '../../blocks/report-anomaly';

import useProjectDetails from '../../../hooks/useProjectDetails';

import authService from '../../../services/authService';
import toastService from '../../../services/toastService';

import { getPIDImages as extractPIDImages, formatTimeWithRelativeStamp, matchPhrases, saveImage, toTitleCase, concatClassNames, getCanCreateReport } from '../../../utils/utilities';
import { PDFImageProps, Project, ProjectComment, User } from '../../../utils/models';
import { getTutorials } from '../../../store/tutorials';
import { PROJECT_STATUS, USER_TYPES, tutorialConstants } from '../../../utils/constants';
import { getCanAccessReportInternally, getCanAccessReportDetails, getIsContactPerson, getCanAccessReportProgress, getClientCanAccessReport } from '../../../utils/session';

import config from '../../../utils/config';
import routes from '../../../utils/routes';

import css from './projects.module.scss';

const Details: React.FC = () => {
	const [acceptReport, setAcceptReport] = useState(false);
	const [approveReport, setApproveReport] = useState(false);
	const [clientReview, setClientReview] = useState(false);
	const [createProject, setCreateProject] = useState(false);
	const [reviewReport, setReviewReport] = useState(false);
	const [isReportUploadVisible, setReportUploadVisible] = useState(false);

	const navigate = useNavigate();
	const user = authService.getCurrentUser() as User;

	const { projectId: id } = useParams();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { projectDetails, ...otherDetails } = useProjectDetails();

	const isContactPerson = useMemo(() => {
		return projectDetails ? getIsContactPerson(projectDetails) : false;
	}, [projectDetails]);

	const canClientReview = useMemo(() => {
		return user && user.userType === USER_TYPES.EXTERNAL && user.role.reviewReports;
	}, []);

	const isSameDivision = useMemo((): boolean => {
		if (!projectDetails) return false;

		const isExternalUser = matchPhrases(user.userType, USER_TYPES.EXTERNAL);
		if (isExternalUser) return true;

		const isSameDivision = user.division.includes(projectDetails.divisionId);
		return isSameDivision;
	}, [projectDetails, user.division, user.userType]);

	const latestComment = useMemo((): ProjectComment | null => {
		if (!projectDetails) return null;

		const comments: ProjectComment[] = projectDetails.comment;
		if (comments.length === 0) return null;

		return _.orderBy(comments, ['commentDate'], ['desc'])[0];
	}, [projectDetails]);

	const isCommentAddressed = useMemo((): boolean => {
		if (!projectDetails || !latestComment) return true;

		if (latestComment.rejectReason) {
			const newValue = _.get(projectDetails, latestComment.rejectReason);
			if (latestComment.oldValue === newValue) return false;
		}

		return true;
	}, [latestComment, projectDetails]);

	const canUpdateReport = useMemo(() => {
		if (projectDetails) {
			if (projectDetails.report && projectDetails.report.isPublished) return false;

			return user.role.approveReports;
		}

		return false;
	}, [projectDetails, user.role.approveReports]);

	const canViewProgressReport = useMemo(() => {
		if (
			!config.enableReportComment ||
			!projectDetails ||
			!projectDetails.report?.savedProgressUrl ||
			!isSameDivision
		)
			return false;

		return getCanAccessReportProgress(projectDetails);
	}, [projectDetails, isSameDivision]);

	const createNewReport = (): void => {
		const projectId: string | undefined = projectDetails?._id;
		if (!projectId) return toastService.error('Project details not available');

		const path: string = `/dashboard/projects/${projectId}/new-report`;
		navigate(path);
	};

	const getCreateButtonText = (): string => {
		return projectDetails?.report?.isPublished
			? 'Update Report'
			: 'Create Report';
	};

	const navigateToProjects = (): void => {
		const { projects, organizationDashboard } = routes;
		const { manageProjects } = user.role;

		const path: string = manageProjects
			? projects.fullPath
			: organizationDashboard.fullPath;

		navigate(path);
	};

	const navigateToProjectProgress = (): void => {
		const path: string = `/dashboard/projects/${id}/progress`;
		navigate(path);
	};

	const renderActivateProject = () => {
		if (!projectDetails || !isSameDivision) return null;

		const isCompleted = matchPhrases(projectDetails.projectStatus, 'completed');
		if (isCompleted || projectDetails.isActive) return null;

		return (
			<Conditional isVisible={user.role.approveReports}>
				<Elevation isElevated={activeTutorial === tutorialConstants.ACTIVATE_PROJECT}>
					<div className={concatClassNames([css['projects-section']])}>
						<h4 className={css.pidLabel}>
							<i className="bi bi-check-all"></i>
							<span>Activate Project</span>
						</h4>

						<ActivateProjectForm project={projectDetails} />
					</div>
				</Elevation>
			</Conditional>
		);
	};

	const renderClientReviewButton = () => {
		if (!projectDetails || !projectDetails.report.isPublished || !projectDetails.report.approved) {
			return null;
		}

		return (
			<Conditional isVisible={canClientReview && projectDetails.clientReviewRequired}>
				<div className={css['accept-button']}>
					<Elevation isElevated={activeTutorial === tutorialConstants.ACCEPT_REPORT}>
						<Button
							label="Review Report"
							icon="patch-check-fill"
							onClick={toggleClientReview}
						/>
					</Elevation>
				</div>
			</Conditional>
		);
	};

	const renderAcceptReportButton = () => {
		if (!projectDetails ||!projectDetails.report.isPublished ||!projectDetails.report.approved) {
			return null;
		}

		const isNotAccepted: boolean = !projectDetails.report.accepted;
		const isPendingClientReview: boolean = projectDetails.clientReviewRequired && !projectDetails.report.isCustomerReviewed;

		return (
			<Conditional isVisible={isContactPerson && isNotAccepted && !isPendingClientReview}>
				<div className={css['accept-button']}>
					<Elevation isElevated={activeTutorial === tutorialConstants.ACCEPT_REPORT}>
						<Button
							label="Accept Report"
							icon="patch-check-fill"
							onClick={toggleAcceptReport}
						/>
					</Elevation>
				</div>
			</Conditional>
		);
	};

	const renderCreateProjectButton = () => {
		return (
			<Conditional isVisible={getCanCreateReport(projectDetails, user)}>
				<Elevation isElevated={activeTutorial === tutorialConstants.CREATE_REPORT}>
					<div className="ms-3">
						<Button
							label={getCreateButtonText()}
							icon="file-earmark-plus-fill"
							onClick={createNewReport}
						/>
					</div>
				</Elevation>
			</Conditional>
		);
	};

	const renderCanApproveReport = () => {
		if (!projectDetails || !isSameDivision) return null;

		const { report } = projectDetails;
		if (!report.isPublished || !report.isReviewed || report.approved) return null;

		return (
			<Conditional isVisible={user.role.approveReports}>
				<Elevation isElevated={activeTutorial === tutorialConstants.APPROVE_REPORT}>
					<div className={css['approve-button']}>
						<Button
							label="Approve Report"
							icon="check-circle-fill"
							onClick={toggleApproveReport}
						/>
					</div>
				</Elevation>
			</Conditional>
		);
	};

	const renderEditProjectButton = () => {
		if (!projectDetails || !isSameDivision) return null;

		if (!latestComment && projectDetails.report.isPublished) return null;

		if (projectDetails.isCompleted) return null;

		if (isCommentAddressed || !user.role.manageProjects || !user.role.approveReports)
			return null;

		return (
			<Elevation isElevated={activeTutorial === tutorialConstants.EDIT_PROJECT}>
				<div className="ms-3">
					<Button
						icon="pencil-fill"
						label="EDIT"
						disabled={false}
						style={{ backgroundColor: '#ab3c3c' }}
						onClick={toggleCreateProject}
					/>
				</div>
			</Elevation>
		);
	};

	const renderPIDImages = () => {
		if (!projectDetails) return null;

		const images: PDFImageProps[] = extractPIDImages(projectDetails.pidImages);
		if (images.length === 0) return null;

		return (
			<React.Fragment>
				<div className="divider my-4"></div>

				<div className={css.pid}>
					<div className="row">
						<h4 className={css.pidLabel}>
							<i className="bi bi-images"></i>
							P&ID Images
						</h4>

						{images.map((image, index) => (
							<Fragment key={image.src}>
								<div className="col">
									<Image
										refNo={image.refNo}
										src={image.src}
										caption={'P&ID Image-' + index + 1}
									/>
								</div>

								<Conditional isVisible={(index + 1) % 3 === 0}>
									<div className="w-100"></div>
								</Conditional>
							</Fragment>
						))}
					</div>
				</div>
			</React.Fragment>
		);
	};

	const renderProjectComments = () => {
		const isInternalUser = matchPhrases(user.userType, USER_TYPES.INTERNAL);
		if (!isInternalUser || !projectDetails) return null;

		const defaultComments: ProjectComment[] = projectDetails.comment;
		if (defaultComments.length === 0) return null;

		return (
			<React.Fragment>
				<div className="divider my-4"></div>

				<ProjectComments comments={defaultComments} />
			</React.Fragment>
		);
	};

	const renderProjectDetails = () => {
		return projectDetails ? <ProjectSummary project={projectDetails} /> : null;
	};

	const renderReviewReportButton = () => {
		if (!projectDetails || !isSameDivision) return null;

		const { report } = projectDetails;
		const { reviewReports } = user.role;

		if (!report.isPublished || !reviewReports) return null;

		return (
			<Conditional isVisible={!report.approved && !report.isReviewed}>
				<Elevation
					isElevated={activeTutorial === tutorialConstants.REVIEW_REPORT}
				>
					<Button
						label="Review Report"
						icon="book-half"
						style={{ backgroundColor: 'green' }}
						onClick={toggleReviewReport}
					/>
				</Elevation>
			</Conditional>
		);
	};

	const renderRequestSupervisorUpdate = () => {
		if (!projectDetails || !isSameDivision) return null;

		const { _id, isActive, supervisorName } = projectDetails;
		const { approveReports, reviewReports } = user.role;

		const supervisorUpdates = projectDetails.comment.filter(({rejectReason}) => rejectReason === 'supervisorName');
		if (supervisorUpdates.length >= 2 || !isActive) return null;

		return (
			<Conditional isVisible={reviewReports || approveReports}>
				<Elevation isElevated={activeTutorial === tutorialConstants.REQUEST_SUPERVISOR_UPDATE}>
					<RequestSupervisorUpdate
						currentSupervisorName={supervisorName}
						projectId={_id}
					/>
				</Elevation>
			</Conditional>
		);
	};

	const renderViewReportButton = () => {
		if (!projectDetails) return null;

		const { report } = projectDetails;
		if (!report?.savedProgressUrl) return null;

		const isInternal = matchPhrases(user.userType, USER_TYPES.INTERNAL);
		if (!isInternal && !getClientCanAccessReport(projectDetails)) return null;

		return (
			<Elevation isElevated={activeTutorial === tutorialConstants.VIEW_REPORT}>
				<Button
					label="View Report PDF"
					icon="file-pdf-fill"
					onClick={() => saveImage(report.savedProgressUrl as string, projectDetails.documentNumber)}
				/>
			</Elevation>
		);
	};
	
	const renderViewCommentDocument = () => {
		if (!projectDetails || !projectDetails.report) return null;

		return (
			<Elevation isElevated={activeTutorial === tutorialConstants.VIEW_REPORT}>
				{projectDetails.report.reviewCommentDocumentUrl && (
					<Button
						style={{ margin: "0", marginRight: "1rem", backgroundColor: "#ff0000" }}
						label="View Client Review Document"
						icon="file-pdf-fill"
						onClick={() => saveImage(projectDetails.report.reviewCommentDocumentUrl!, projectDetails.documentNumber)}
					/>
				)}

				{projectDetails.report.commentDocumentUrl && (
					<Button
						style={{ margin: "0", backgroundColor: "#1D6F42" }}
						label="View Client Acceptance Document"
						icon="file-pdf-fill"
						onClick={() => saveImage(projectDetails.report.commentDocumentUrl!, projectDetails.documentNumber)}
					/>
				)}
			</Elevation>
		);
	};

	const renderViewAnomalyRecommendationPDF = () => {
		if (!projectDetails || !projectDetails.projectUrl) return null;

		return (
			<div className="ms-3">
				<Button
					style={{ backgroundColor: '#ab3c3c' }}
					label="View Summary"
					icon="file-pdf-fill"
					onClick={() => saveImage(projectDetails.projectUrl, projectDetails.documentNumber)}
				/>
			</div>
		);
	};

	const renderDownloadExcelFileButton = () => {
		if (!projectDetails || !projectDetails.report?.excelDocumentUrl)
			return null;

		return (
			<Conditional isVisible={getCanAccessReportInternally(projectDetails)}>
				<div className="me-3">
					<Button
						style={{ backgroundColor: '#000000' }}
						label="Download Excel File"
						icon="file-earmark-spreadsheet-fill"
						onClick={() =>
							saveImage(
								projectDetails.report?.excelDocumentUrl as string,
								projectDetails.documentNumber
							)
						}
					/>
				</div>
			</Conditional>
		);
	};

	const renderViewProgressReport = () => {
		if (!canViewProgressReport) return null;

		const label = projectDetails?.isActive
			? 'Comment on Project Report'
			: 'View Comments on Project Report';

		return (
			<Elevation isElevated={activeTutorial === tutorialConstants.VIEW_PROJECT_PROGRESS}>
				<Button
					style={{ backgroundColor: '#ab3c3c' }}
					icon="calendar-range-fill"
					label={label}
					onClick={navigateToProjectProgress}
				/>
			</Elevation>
		);
	};

	const renderUpdateNotice = () => {
		if (!projectDetails || !latestComment) return null;

		const rejectionReason = latestComment.rejectReason as keyof Project;
		const updatedValue = projectDetails[rejectionReason] as string;

		const isValueUpdated = latestComment.oldValue !== updatedValue;
		if (!latestComment.oldValue || !updatedValue || !isValueUpdated)
			return null;

		return (
			<Conditional isVisible={matchPhrases(user.userType, USER_TYPES.INTERNAL) && (user.role.approveReports || user.role.reviewReports)}>
				<Notice title="Update Notice!">
					<p>
						The <span className="text-uppercase">{rejectionReason}</span> was
						updated from{' '}
						<span className="text-danger">{latestComment.oldValue}</span>{' '}
						<i className="bi bi-arrow-right"></i> <span>{updatedValue}</span>
					</p>
				</Notice>

				<div className="divider my-4"></div>
			</Conditional>
		);
	};

	const renderNotificationBell = () => {
		if (!projectDetails || !canViewProgressReport) return null;

		const comments = projectDetails.report.comment ?? [];
		if (!projectDetails.report.showComment || !comments.length) {
			return null;
		}

		return (
			<NotificationBell
				notifications={comments.length}
				onClick={navigateToProjectProgress}
			/>
		);
	};

	const renderSupervisoryButtons = () => {
		if (!projectDetails || !projectDetails.report.savedProgressUrl) return null;

		return (
			<Conditional isVisible={projectDetails.report.isPublished}>
				<div className="divider my-4"></div>

				<div
					className={concatClassNames([
						css['projects-section'],
						css['projects-section-supervisory-buttons'],
						'd-flex',
						'p-2 justify-content-between',
					])}
				>
					<div className="d-flex justify-content-center align-items-center bg-light border p-2">
						{renderDownloadExcelFileButton()}

						{renderViewReportButton()}

						{renderViewAnomalyRecommendationPDF()}

						<ReportAnomaly />
					</div>

					<Conditional isVisible={projectDetails.projectStatus !== PROJECT_STATUS.UNDER_CORRECTION}>
						<div className="d-flex justify-content-center align-items-center bg-light border p-2">
							{renderReviewReportButton()}

							{renderCanApproveReport()}

							{renderClientReviewButton()}

							{renderAcceptReportButton()}
						</div>
					</Conditional>
				</div>
			</Conditional>
		);
	};

	const renderProjectTimeline = () => {
		if (!projectDetails) return null;

		const STATUSES = Object.keys(PROJECT_STATUS).map((key) => PROJECT_STATUS[key as keyof typeof PROJECT_STATUS]);
		const currentStatusIndex = STATUSES.findIndex((status) => matchPhrases(status, projectDetails.projectStatus));
		
		return <Timeline labels={STATUSES} currentIndex={currentStatusIndex} />;
	};

	const handleReportUpload = () => {
		toastService.success('Report Updated Successfully!');
		window.location.reload();
	};

	const toggleAcceptReport = (): void => {
		if (user.signature) setAcceptReport((p: boolean) => !p);
		else toastService.error('Please configure your signature first!');
	};

	const toggleClientReview = (): void => {
		if (user.signature) setClientReview((p: boolean) => !p);
		else toastService.error('Please configure your signature first!');
	};

	const toggleApproveReport = (): void => {
		if (user.signature) setApproveReport((p: boolean) => !p);
		else toastService.error('Please configure your signature first!');
	};

	const toggleCreateProject = (): void => {
		setCreateProject((prev: boolean) => !prev);
	};

	const toggleReviewReport = (): void => {
		if (user.signature) setReviewReport((p: boolean) => !p);
		else toastService.error('Please configure your signature first!');
	};

	const toggleReportUpload = (): void => {
		setReportUploadVisible((previous: boolean) => !previous);
	};

	useEffect(() => {
		if (projectDetails && !getCanAccessReportDetails(projectDetails)) {
			navigate(routes.projects.fullPath);
		}
	}, [projectDetails, navigate]);

	return (
		<Fragment>
			<Modal style={{ height: 'auto' }} isVisible={clientReview} onClose={toggleClientReview}>
				<ClientReviewForm project={projectDetails} />
			</Modal>

			<Modal style={{ height: 'auto' }} isVisible={acceptReport} onClose={toggleAcceptReport}>
				<AcceptReportForm project={projectDetails} />
			</Modal>

			<Modal  isVisible={isReportUploadVisible} onClose={toggleReportUpload}>
				<div className="bg-light border p-2">
					<div className="bg-white border p-2">
						<UploadReportPDF project={projectDetails} onUploadReport={handleReportUpload} />
					</div>
				</div>
			</Modal>

			<Modal
				style={{ height: 'auto' }}
				isVisible={approveReport}
				onClose={toggleApproveReport}
			>
				<ApproveReportForm project={projectDetails} />
			</Modal>

			<Modal
				style={{ height: 'auto' }}
				isVisible={reviewReport}
				onClose={toggleReviewReport}
			>
				<ReviewReportForm project={projectDetails} />
			</Modal>

			<Modal isVisible={createProject} onClose={toggleCreateProject}>
				<UpdateProject project={projectDetails} />
			</Modal>

			<Header
				canViewCTA
				onCTAClick={navigateToProjects}
				subtitle={'Project Title: ' + (projectDetails?.projectTitle ?? 'N/A')}
				buttonIcon="kanban-fill"
				buttonTitle="Back to Projects"
			/>

			<div className={css['projects-body']}>
				<Loader isVisible={otherDetails.isFetching} />

				<Conditional isVisible={otherDetails.error}>
					<div className="bg-white border p-3 d-flex justify-content-center mb-4">
						<Button
							label="Retry"
							disabled={otherDetails.isFetching}
							onClick={otherDetails.fetchProjectDetails}
						/>
					</div>
				</Conditional>

				<Conditional isVisible={projectDetails !== null}>
					<ProjectDetailsInstruction />

					<div className="d-flex justify-content-end">
						<Elevation isElevated={activeTutorial === tutorialConstants.ACTIVATE_PROJECT}>
							<Status isActive={projectDetails?.isActive as boolean} />
						</Elevation>

						<div className="ml-3">
							<SyncTime lastSynced={otherDetails.lastUpdated} />
						</div>
					</div>

					<div className="divider my-4"></div>

					{renderProjectTimeline()}

					<Conditional isVisible={isCommentAddressed}>
						<div className="mt-4 d-flex justify-content-end align-items-center">
							<div>{renderNotificationBell()}</div>
						</div>
					</Conditional>
					
						<div className="divider my-4"></div>

						<div className="d-flex justify-content-end w-auto bg-white p-2 border gx-3">
							<div className="p-2 bg-light border">
								{renderViewCommentDocument()}
							</div>

							<Conditional isVisible={canUpdateReport}>
								<div className="p-2 bg-light border ms-2">
									<Button
										icon="pencil"
										label="Update PDF Report"
										onClick={toggleReportUpload}
										style={{ backgroundColor: '#ab3c3c', marginTop: 0 }}
									/>
								</div>
							</Conditional>
						</div>

					<div className="divider my-4"></div>

					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className={concatClassNames([css['projects-section'], 'col p-3'])}>
								<div className="bg-light p-3 border">
									<div className={css['summary-detail']}>
										<div className={css['summary-detail-left']}>
											<h2>{toTitleCase(projectDetails?.projectTitle)}</h2>

											<div className={css['summary-detail-left-item']}>
												{projectDetails?.location}
											</div>

											<div className={css['summary-detail-left-item']}>
												Created:{' '} {formatTimeWithRelativeStamp(projectDetails?.createdAt as string)}
											</div>
										</div>

										<div className={css['summary-detail-right']}>
											<div className="d-flex justify-content-end">
												<Conditional isVisible={matchPhrases(user.userType, USER_TYPES.INTERNAL) && user.role.reviewReports}>
													<DuplicateReport />
												</Conditional>

												<div className="d-flex justify-content-end">
													<div>
													 	<ProjectStatusButton isActive status={projectDetails?.projectStatus as string} />
													</div>
												</div>
											</div>

											<div className="d-flex justify-content-end">
												{renderViewProgressReport()}

												<Conditional isVisible={isCommentAddressed}>
													{renderCreateProjectButton()}
												</Conditional>

												{renderEditProjectButton()}
											</div>
										</div>
										</div>
									</div>
							</div>
						</div>
					</div>

					<Conditional isVisible={isCommentAddressed}>
						{renderRequestSupervisorUpdate()}

						{renderSupervisoryButtons()}
					</Conditional>

					<Conditional isVisible={matchPhrases(user.userType, USER_TYPES.INTERNAL) && !isCommentAddressed}>
						<div className="divider my-4"></div>

						<Notice title="Unaddressed Comments!">
							Comments must first be addressed before further action can be
							taken on this project
						</Notice>
					</Conditional>

					<div className="divider my-4"></div>

					{renderProjectDetails()}

					{renderPIDImages()}

					{renderProjectComments()}

					<AuditTrail reportId={projectDetails?.report?._id as string} />

					<div className="divider my-4"></div>

					<Conditional isVisible={isCommentAddressed}>
						{renderUpdateNotice()}

						{renderActivateProject()}
					</Conditional>
				</Conditional>
			</div>
		</Fragment>
	);
};

export default Details;
